/**
 * cloudSession — the cloud viewer's data model for the `/ve/login` payload.
 *
 * `/ve/login` returns a rich payload: { user, customers, projects, devices,
 * cameras, userViews, ptzPresets, token, backendVersion }. The viewer used to
 * keep only token + cameras and drop the rest, so the Jump selector had no
 * devices and the view picker had no views. This module normalizes the WHOLE
 * payload ONCE into a structured session that the CloudNvrAdapter holds; the UI
 * then reads it through the adapter's accessors (getJumpTargets / getGridCameras
 * / getUser / getViews) instead of re-deriving or re-fetching it.
 *
 * Cloud "jump" = switch the ACTIVE DEVICE. One JWT authorizes every device in
 * its `devices` claim, so jumping is just changing which device's cameras the
 * grid shows — a device-id change in the stream URLs, no re-auth. (Contrast the
 * local NVR jump, which opens a fresh per-server session.)
 *
 * Everything here is pure (payload in → model out), so it is unit-testable
 * without the network or Redux.
 */

import { classifyDevice } from './cloudUrls';

/**
 * Normalize one `/ve` device record to the viewer's device shape. `raw` is kept
 * so capability classification (cloudUrls.classifyDevice) can still read the
 * original config/type fields.
 * @param {Object} d - a raw `/ve` device record
 * @returns {{id:string, name:string, online:boolean, customerName:string,
 *   projectName:string, customerId:*, projectId:*, cameraCount:*, raw:Object}}
 */
export const normalizeDevice = (d = {}) => {
  const id = d.sDeviceID || d.deviceId || null;
  return {
    id,
    bID: d.bID,           // JSDevice PK — the basis of the synthetic view serial (bID + 9999)
    name: d.sName || d.sDeviceName || id || 'Device',
    online: d.fOnline === 1 || d.fOnline === true,
    // CloudAPI publishes the capability as fDragon. Keep support for isModern,
    // device-type strings, and config markers through the shared classifier.
    isModern: classifyDevice(d).directMedia,
    customerName: d.sCustomerName || '',
    projectName: d.sProjectName || '',
    customerId: d.bCustomerID,
    projectId: d.bCustomerProjectID,
    cameraCount: d.bCameras,
    raw: d,
  };
};

/**
 * Synthetic per-device "serial" for the shared view machinery.
 *
 * The dview view system (ViewActions/selectView/ViewReducer) keys every camera by
 * (bSerial, bCamera): a view slot stores `{bSerial, bCamera}` and selectView matches
 * a grid camera where `c.bSerial === slot.bSerial && c.bID === slot.bCamera` (eView
 * path). Cloud has no NVR serials — devices are identified by string `sDeviceID` — so
 * we mint a stable numeric serial from the device's JSDevice PK: **bID + 9999**
 * (David's convention; the +9999 offset keeps it clear of real NVR serials). This is
 * the ONE mapping that lets the entire view machinery run UNCHANGED for cloud — the
 * cloud-ness is confined to this offset plus CRUD routing through the adapter.
 */
export const CLOUD_SERIAL_OFFSET = 9999;

/** device JSDevice bID → the synthetic view serial (bID + 9999). 0 if bID is absent. */
export const deviceBidToSerial = (bID) => (bID == null ? 0 : Number(bID) + CLOUD_SERIAL_OFFSET);

/**
 * Normalize the full `/ve/login` payload into the cloud session model.
 * @param {Object} payload - the `/ve/login` response body
 * @returns {{token:string|null, backendVersion:*, user:Object|null,
 *   devices:Array, devicesById:Object, cameras:Array, camerasByDeviceId:Object,
 *   views:Array, customers:Array, projects:Array, ptzPresets:Array}}
 */
export const mapLoginPayload = (payload = {}) => {
  const devices = (payload.devices || []).map(normalizeDevice).filter((d) => d.id);
  const devicesById = {};
  for (const d of devices) devicesById[d.id] = d;

  const cameras = payload.cameras || [];
  const camerasByDeviceId = {};
  for (const c of cameras) {
    const id = c.sDeviceID;
    if (!id) continue;
    if (!camerasByDeviceId[id]) camerasByDeviceId[id] = [];
    camerasByDeviceId[id].push(c);
  }

  // Projects carry the cloud-storage base URL + timezone used to build recorded-clip
  // URLs and convert the device-local playback window to the UTC the events are stored
  // in (see CloudNvrAdapter.fetchPlaybackEvents). Keyed by bID; a device references its
  // project via projectId (bCustomerProjectID).
  const projects = payload.projects || [];
  const projectsById = {};
  for (const p of projects) if (p && p.bID != null) projectsById[p.bID] = p;

  return {
    token: payload.token || null,
    backendVersion: payload.backendVersion,
    user: payload.user || null,
    devices,
    devicesById,
    cameras,
    camerasByDeviceId,
    views: payload.userViews || [],
    customers: payload.customers || [],
    projects,
    projectsById,
    ptzPresets: payload.ptzPresets || [],
    // Per-device rights { sDeviceID → {fAdmin,fPlayback,…} } (cloudapi auth.js login `deviceRights`) — the
    // authoritative per-(user,device) flags; Setup gates device/site editability on it. null when an
    // older backend omits it (callers then fall back to the coarse `rights`).
    deviceRights: (payload.deviceRights && typeof payload.deviceRights === 'object') ? payload.deviceRights : null,
  };
};

/**
 * Default active device on login. Prefers an ONLINE device that has cameras so
 * the initial grid shows a live device (not one whose cameras are dead); then
 * falls back to any device with cameras, then the first device, then null.
 * @param {Object} session - a mapLoginPayload() result
 * @returns {string|null}
 */
export const defaultActiveDeviceId = (session) => {
  if (!session || !session.devices.length) return null;
  const hasCams = (d) => (session.camerasByDeviceId[d.id] || []).length > 0;
  const onlineWithCams = session.devices.find((d) => d.online && hasCams(d));
  if (onlineWithCams) return onlineWithCams.id;
  const withCams = session.devices.find(hasCams);
  return (withCams || session.devices[0]).id;
};

/**
 * Map a normalized device → a Jump-selector target (what the cloud Jump dropdown
 * lists). `siteLabel` is the customer/project pair for grouping/among-devices
 * display; `isActive` marks the device currently in the grid.
 * @param {Object} device - a normalizeDevice() result
 * @param {string} [activeDeviceId]
 */
export const deviceToJumpTarget = (device = {}, activeDeviceId) => ({
  id: device.id,
  name: device.name,
  online: device.online,
  siteLabel: [device.customerName, device.projectName].filter(Boolean).join(' — '),
  isActive: device.id === activeDeviceId,
});

/**
 * Map ONE device's login-shape cameras → the ServerReducer SET_ALL_CAMERAS grid
 * shape (bID/sName/fEnable/fPtz/bSerial/bCamera/bProfile/sDeviceID/source/deviceOnline).
 * Deduped by (device, cam#) with the first profile winning — so a camera listed
 * across profiles 1/2 becomes one grid cell (the live grid picks the profile).
 * @param {Array<Object>} cameras - the active device's `/ve` camera records
 * @param {Object} [device] - the normalized device (for deviceOnline)
 * @returns {Array<Object>} cameras shaped for the live grid
 */
// ---- refresh persistence -------------------------------------------------
// The CloudNvrAdapter holds the whole session in memory; a page reload makes a
// FRESH singleton with a null session, which breaks device classification (a
// dragon device misreads as legacy), the grid, and every clip URL. redux-persist
// saves the token + grid cameras but NOT this model, so we persist it here and
// rehydrate the adapter in its constructor — no re-login, no /ve round-trip.
// The payload is plain data (mapLoginPayload output), so it JSON-serializes.
export const CLOUD_SESSION_KEY = 'dl_cloud_session';
const REDUX_PERSIST_KEY = 'persist:root';

const hasPtzCapability = (cam) => cam
  && Object.prototype.hasOwnProperty.call(cam, 'fPtz');

const sessionCamerasAreCurrent = (cameras) => Array.isArray(cameras)
  && cameras.every(hasPtzCapability);

const reduxCamerasAreCurrent = (cameras) => Array.isArray(cameras)
  && cameras.every((cam) => hasPtzCapability(cam)
    || (cam && cam.fEnable === false && !cam.sDeviceID));

const reduxCameraCacheIsCurrent = (raw) => {
  if (!raw) return true;
  const root = JSON.parse(raw);
  const server = typeof root.server === 'string' ? JSON.parse(root.server) : root.server;
  if (!server) return true;
  const collections = [server.cameras, server.allCameras].filter(Array.isArray);
  return collections.every(reduxCamerasAreCurrent);
};

const clearLegacyCloudCaches = () => {
  localStorage.removeItem(CLOUD_SESSION_KEY);
  localStorage.removeItem(REDUX_PERSIST_KEY);
};

/** Persist the cloud session bundle for rehydration after a refresh. */
export const saveCloudSession = ({ token, refreshToken, session, activeDeviceId } = {}) => {
  try {
    if (!session) { localStorage.removeItem(CLOUD_SESSION_KEY); return; }
    // refreshToken is portal Microsoft OAuth only (absent for password login).
    localStorage.setItem(CLOUD_SESSION_KEY, JSON.stringify({ token, refreshToken, session, activeDeviceId }));
  } catch (e) { /* storage unavailable / quota — non-fatal, refresh just won't restore */ }
};

/** Load the persisted cloud session bundle, or null. */
export const loadCloudSession = () => {
  try {
    const raw = localStorage.getItem(CLOUD_SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const reduxCurrent = reduxCameraCacheIsCurrent(localStorage.getItem(REDUX_PERSIST_KEY));
    if (!parsed || !parsed.session) {
      if (!reduxCurrent) clearLegacyCloudCaches();
      return null;
    }

    // Pre-fPtz cloud sessions cannot safely restore. Their Redux camera grid also
    // lacks the capability, so clear both copies before PersistGate rehydrates and
    // force one fresh CloudAPI login.
    const cameras = parsed.session.cameras;
    if (!sessionCamerasAreCurrent(cameras) || !reduxCurrent) {
      clearLegacyCloudCaches();
      return null;
    }
    return parsed;
  } catch (e) {
    try { clearLegacyCloudCaches(); } catch (storageError) { /* ignore */ }
    return null;
  }
};

/** Drop the persisted cloud session (logout / session expiry). */
export const clearCloudSession = () => {
  try { localStorage.removeItem(CLOUD_SESSION_KEY); } catch (e) { /* ignore */ }
};

export const deviceCamerasToGrid = (cameras = [], device) => {
  const seen = new Set();
  const grid = [];
  for (const cam of cameras) {
    const key = `${cam.sDeviceID}-${cam.bCamera}`;
    if (seen.has(key)) continue;
    seen.add(key);
    grid.push({
      bID: grid.length + 1,
      sName: cam.sName || `Cam ${cam.bCamera}`,
      fEnable: cam.fEnabled != null ? !!cam.fEnabled : true,
      fPtz: !!cam.fPtz,
      bSerial: 0,
      bCamera: cam.bCamera,
      bProfile: cam.bProfile,
      sDeviceID: cam.sDeviceID,
      source: cam.source,
      deviceOnline: device && device.online ? 1 : 0,
      isModern: !!(device && device.isModern),
    });
  }
  return grid;
};

// ---- all-devices grid + cloud views ↔ view-machinery slots -----------------
// The cloud viewer surfaces EVERY device's cameras at once (David's "all-devices
// grid" decision — the eView `allCameras` model — so a saved view can span devices),
// and drives the SHARED view machinery (ViewActions/selectView/ViewReducer/
// ViewDropdown) unchanged by minting a per-device serial (deviceBidToSerial) and
// mapping the cloud `sCams` shape ⇆ the local view-slot shape here, at the adapter
// boundary. Everything below is pure (session/data in → data out), unit-testable
// without the network or Redux.

/**
 * Every accessible device's cameras, shaped for the live grid AND for use as the
 * cross-device camera pool the view machinery looks up against (the cloud analog of
 * `state.server.allCameras` in eView). Each camera carries the synthetic
 * `bSerial = deviceBidToSerial(device.bID)` and `bID = bCamera` so selectView's
 * eView match (`c.bSerial === slot.bSerial && c.bID === slot.bCamera`) resolves it,
 * plus `sDeviceID`/`bCamera`/`bProfile` for the stream-URL builders. Deduped per
 * (device, cam#) with the first profile winning (a camera listed across profiles
 * 1/2 → one cell), matching deviceCamerasToGrid. `sServerName` groups by device.
 * @param {Object} session - a mapLoginPayload() result
 * @returns {Array<Object>} cameras for every device
 */
export const allDevicesToGrid = (session) => {
  if (!session || !Array.isArray(session.devices)) return [];
  const grid = [];
  for (const device of session.devices) {
    const cams = (session.camerasByDeviceId && session.camerasByDeviceId[device.id]) || [];
    const seen = new Set();
    for (const cam of cams) {
      const key = `${cam.sDeviceID}-${cam.bCamera}`;
      if (seen.has(key)) continue;
      seen.add(key);
      grid.push({
        bID: cam.bCamera,                       // = the view slot's bCamera (selectView match)
        sName: cam.sName || `Cam ${cam.bCamera}`,
        fEnable: cam.fEnabled != null ? !!cam.fEnabled : true,
        fPtz: !!cam.fPtz,
        bSerial: deviceBidToSerial(device.bID), // per-device serial (bID + 9999)
        bCamera: cam.bCamera,
        bProfile: cam.bProfile,
        sDeviceID: cam.sDeviceID,
        source: cam.source,
        deviceOnline: device.online ? 1 : 0,
        sServerName: device.name,               // device label (eView groups by server name)
        isModern: !!device.isModern,
      });
    }
  }
  return grid;
};

/**
 * One cloud view's `cams` array → the local view-slot list. Cloud stores a view's
 * cameras as `sCams = [{deviceId, cam, profile, slot?}]` (string deviceId, no serial);
 * the machinery wants `[{ixSlot, bSerial, bCamera, sFisheye}]`. We map
 * deviceId → bSerial via the session's device index (bID + 9999) and preserve the
 * grid position from `slot` (falling back to array order when a legacy row lacks it,
 * so gaps in a saved layout survive a round-trip). A cam whose device is NOT in the
 * session (access revoked, or a stale view) is skipped — the defensive base of the
 * Phase-4 revocation filter.
 * @param {Array<Object>} cams - a view's cloud sCams array
 * @param {Object} session - a mapLoginPayload() result (for deviceId → bID)
 * @returns {Array<{ixSlot:number, bSerial:number, bCamera:*, sFisheye:string}>}
 */
export const viewCamsToSlotList = (cams = [], session) => {
  const byId = (session && session.devicesById) || {};
  const out = [];
  (cams || []).forEach((c, i) => {
    if (!c) return;
    const dev = byId[c.deviceId];
    if (!dev) return; // device not accessible / not in session — skip (Phase-4 revocation basis)
    out.push({
      ixSlot: c.slot != null ? c.slot : i,
      bSerial: deviceBidToSerial(dev.bID),
      bCamera: c.cam,
      sFisheye: c.fisheye || c.sFisheye || '',
    });
  });
  return out;
};

/**
 * All the session's cloud views flattened into the `config.user.getSets` row shape
 * the getViews thunk parses — one row per (view, camera): `{sName, ixSlot, bSerial,
 * bCamera, sFisheye, bUser, fDefault, fDividia}`. The normalized backend delivers each view
 * with its header flags, so those map straight through: `bUser = bUserID` (0 = global/shared →
 * the organizer's Global section; else = the owning user → User section),
 * `fDefault = the view's fDefault` (→ Default section, and it suppresses the client-
 * computed per-device default for the device(s) it covers, §7), and `fDividia = the view's
 * fDividia` (Dividia-internal — carried so the organizer/edit/unsaved-save keep it, §12 #6).
 * Per-device Default and
 * cross-device Smart views are COMPUTED by the getViews thunk (never stored/delivered),
 * so they aren't produced here. This is the cloud analog of the local getSets result, so
 * the getViews thunk's parse/organize body is reused unchanged. A view with no resolvable
 * cameras produces no rows (the §5 access filter — an all-inaccessible-device view drops),
 * same as a local view with no slots.
 * @param {Object} session - a mapLoginPayload() result (session.views = login userViews)
 * @returns {Array<Object>} getSets-shaped slot rows across all views
 */
export const sessionViewsToSlotRows = (session) => {
  if (!session || !Array.isArray(session.views)) return [];
  const rows = [];
  for (const view of session.views) {
    if (!view) continue;
    const name = view.label || view.sName || '';
    if (!name) continue;
    const bUser = view.bUserID != null ? Number(view.bUserID) : 0;   // 0 = global · else = owner
    const fDefault = view.fDefault ? 1 : 0;
    // Carry fDividia (Dividia-internal, bRole=1 audience) so a re-save can't silently drop it:
    // the getViews organizer copies it onto the view-list item, and the ViewDropdown unsaved-
    // changes "Save" + the edit modal read it back. Without it, re-saving a staff-only view
    // flips it to customer-facing (#6, unified-viewer-cloud-views.md §12).
    const fDividia = view.fDividia ? 1 : 0;
    const slots = viewCamsToSlotList(view.cams, session);
    for (const s of slots) {
      rows.push({
        sName: name,
        ixSlot: s.ixSlot,
        bSerial: s.bSerial,
        bCamera: s.bCamera,
        sFisheye: s.sFisheye,
        bUser,
        fDefault,
        fDividia,
      });
    }
  }
  return rows;
};

/**
 * The current live-grid layout → the cloud `sCams` array for a save. Reads the grid
 * camera objects directly (each carries `sDeviceID`/`bCamera`/`bProfile` from
 * allDevicesToGrid, preserved through selectView + assignCameraToSlot), so no reverse
 * serial lookup is needed. Only enabled, device-bearing cells are emitted (empty slots
 * are skipped); each records its grid position as `slot` so gaps survive the round-trip.
 * @param {Array<Object>} cameras - the 64-slot layout (state.server.cameras)
 * @returns {Array<{deviceId:string, cam:*, profile:number, slot:number}>}
 */
export const layoutToViewCams = (cameras = []) => {
  const out = [];
  (cameras || []).forEach((cam, i) => {
    if (cam && cam.fEnable && cam.sDeviceID) {
      out.push({
        deviceId: cam.sDeviceID,
        cam: cam.bCamera,
        profile: cam.bProfile != null ? cam.bProfile : 1,
        slot: i,
        fisheye: cam.sFisheye || '',
      });
    }
  });
  return out;
};
